import { useState } from "react"

import { Bee } from "~/src/components/BeeKit"
import { Modal, ModalCloseButton, ModalHeader } from "~/src/components/BeeKit/Modal"
import { RailsForm } from "~/src/components/RailsForm"
import { appClient } from "~/src/lib/appClients"
import { iname } from "~/src/lib/iname"

type MessageContactFormProps = {
  contactType?: string
  email?: string
  message?: string
  name?: string
  phone?: string
}

const nameBuilder = iname("messageContact")

export function MessageContactForm(props: MessageContactFormProps) {
  const { name, email, phone, contactType, message } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]> | null>(null)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)

    try {
      const response = await appClient.post("/message_contacts.json", formData)

      const { flash } = response?.data ?? {}

      if (flash != null) {
        Bee.toast.success({ message: flash.success })
      }

      setIsModalOpen(false)
    } catch (error) {
      const { errors } = error?.response?.data ?? {}

      if (errors != null) setErrors(errors)
    }
  }

  return (
    <>
      {/* Trigger Button */}
      <Bee.Button
        type="button"
        kind="ghost"
        className="cursor-pointer text-blue-600"
        onClick={() => setIsModalOpen(true)}
      >
        contact us
      </Bee.Button>

      {/* Modal */}
      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)} className="w-full max-w-lg">
          <ModalHeader className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-lg font-semibold text-gray-900">Contact Us</h2>
            <ModalCloseButton />
          </ModalHeader>

          <div className="p-6 pt-2">
            <p id="intro" className="mb-4 text-gray-600">
              Let us know how we can help and we&apos;ll be in touch right away.
            </p>

            <RailsForm action="/message_contacts" method="post" className="space-y-4" onSubmit={handleSubmit}>
              {/* Name field */}
              <div className="flex flex-col gap-4">
                <Bee.FieldProvider name={nameBuilder.name} errorMessages={errors?.name}>
                  <div className="w-full">
                    <Bee.Input
                      type="text"
                      placeholder="Your name"
                      defaultValue={name}
                      autoComplete="off"
                      className="w-full"
                    />
                    <Bee.FieldErrors cedeSpace />
                  </div>
                </Bee.FieldProvider>

                {/* Email and Phone row */}
                <div className="flex items-start gap-2">
                  <Bee.FieldProvider name={nameBuilder.email} errorMessages={errors?.email}>
                    <div className="w-full">
                      <Bee.Input
                        type="email"
                        placeholder="Your email"
                        defaultValue={email}
                        autoComplete="off"
                        className="w-full"
                      />
                      <Bee.FieldErrors cedeSpace />
                    </div>
                  </Bee.FieldProvider>

                  <Bee.Input
                    className="w-full"
                    type="tel"
                    name={nameBuilder.phone}
                    placeholder="Your phone (optional)"
                    defaultValue={phone}
                    autoComplete="off"
                  />
                </div>

                <select
                  name={nameBuilder.contactType._}
                  defaultValue={contactType}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">How can we help?</option>
                  <option value="Client inquiry">Client inquiry</option>
                  <option value="Vendor inquiry">Vendor inquiry</option>
                  <option value="Press inquiry">Press inquiry</option>
                  <option value="Other">Something else</option>
                </select>

                {/* Message textarea */}
                <Bee.TextArea
                  name={nameBuilder.message}
                  placeholder="Your message (optional)"
                  defaultValue={message}
                  rows={4}
                />
              </div>

              {/* Submit button */}
              <Bee.Button type="submit">Send!</Bee.Button>
            </RailsForm>
          </div>
        </Modal>
      )}
    </>
  )
}
